import { Pto } from '@outcoreaix/pto'
import { apiSlice } from './api-splice'
import { getQueryParamString } from 'utils/get-query-param-string'

export const analyticsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAnalyzedChatsCount: builder.query<number, { userId: number } & Pto.Analytics.AnalyzedChatCountQuery>({
      query: ({ userId, ...rest }) => ({
        url: `analytics/analyzed-chats/count/${userId}`,
        method: 'GET',
        params: rest
      })
    }),
    getAnalyzedChats: builder.query<
      Pto.Analytics.AnalyzedChatList,
      { userId: number } & Pto.Analytics.AnalyzedChatListQuery
    >({
      query: ({ userId, ...params }) => ({
        url: `analytics/analyzed-chats/${userId}`,
        method: 'GET',
        params
      }),
      providesTags: ['AnalyzedChats']
    }),
    getChatSessionsCount: builder.query<number, { userId: number; scope: Pto.Chats.ChatScope; scopeId: number }>({
      query: ({ userId, ...params }) => ({
        url: `analytics/chat-sessions/count/${userId}`,
        method: 'GET',
        params
      })
    }),
    getPersonalizedStarters: builder.query<Pto.AppTemplates.StarterList, number>({
      query: (spaceId) => ({
        url: `analytics/personalized-starters/${spaceId}`,
        method: 'GET'
      }),
      providesTags: ['PersonalizedStarters']
    }),
    resetAnalyzedChats: builder.mutation<void, void>({
      query: () => ({
        url: 'analytics/analyzed-chats',
        method: 'DELETE'
      }),
      invalidatesTags: ['AnalyzedChats']
    }),
    getDashboardFilters: builder.query<Pto.Analytics.DashboardFilters, number>({
      query: (workspaceId) => ({
        url: `analytics/dashboard-filters/${workspaceId}`,
        method: 'GET'
      }),
      providesTags: ['Spaces', 'SpaceMembers']
    }),
    getDashboardTutorOptions: builder.query<
      Pto.Analytics.DashboardTutorOptionsResponse,
      { workspaceId: number; searchText?: string; limit?: number; ids?: string[] }
    >({
      query: ({ workspaceId, ...params }) => {
        const queryParams = getQueryParamString(params)
        return {
          url: `analytics/dashboard-tutor-options/${workspaceId}${queryParams ? `?${queryParams}` : ''}`,
          method: 'GET'
        }
      }
    }),
    getDashboardData: builder.query<
      Pto.Analytics.DashboardData,
      Pto.Analytics.DashboardDataQuery & { workspaceId: number }
    >({
      query: ({ workspaceId, ...params }) => {
        const queryParams = getQueryParamString(params)
        return {
          url: `analytics/dashboard-data/${workspaceId}`,
          method: 'GET',
          params: queryParams
        }
      },
      providesTags: ['Spaces', 'SpaceMembers', 'Starters']
    }),
    getChatsCsv: builder.query<{ downloadUrl?: string }, Pto.Analytics.GetChatsCsv & { spaceId: number }>({
      query: ({ spaceId, ...params }) => {
        const queryParams = getQueryParamString(params)
        return {
          url: `analytics/chats-csv/${spaceId}`,
          method: 'GET',
          params: queryParams
        }
      }
    }),
    getLearnerEngagementFileInfo: builder.query<Pto.Analytics.EngagementFileInfo, { spaceId: number }>({
      query: ({ spaceId }) => {
        return {
          url: `analytics/${spaceId}/learner-engagement`,
          method: 'GET'
        }
      },
      providesTags: ['LearnerEngagement']
    }),
    requestLearnerEngagementGeneration: builder.mutation<
      Pto.Analytics.EngagementFileInfo,
      Pto.Analytics.GenerateEngagement & { spaceId: number }
    >({
      query: ({ spaceId, ...params }) => {
        const queryParams = getQueryParamString(params)
        return {
          url: `analytics/${spaceId}/learner-engagement/?${queryParams}`,
          method: 'POST'
        }
      },
      invalidatesTags: ['LearnerEngagement']
    }),
    getChangelogUnreadCount: builder.query<
      Pto.Analytics.ChangelogUnreadStatus,
      Pto.Analytics.ChangelogUnreadCountQuery
    >({
      query: ({ workspaceId }) => ({
        url: 'analytics/changelogs/unread-count',
        method: 'GET',
        params: { workspaceId }
      }),
      providesTags: ['ChangelogUnread']
    }),
    markChangelogsRead: builder.mutation<void, Pto.Analytics.MarkChangelogsRead>({
      query: (body) => ({
        url: 'analytics/changelogs/read',
        method: 'POST',
        body,
        // The last batch is flushed while the page is unloading, so the request must outlive it.
        keepalive: true
      }),
      invalidatesTags: ['ChangelogUnread', 'ChangelogUserReadAudit']
    }),
    getChangelogUserReadsAudit: builder.query<
      Pto.Analytics.ChangelogUserReadAuditList,
      Pto.Analytics.ChangelogUserReadAuditListQuery | void
    >({
      query: (params) => ({
        url: 'analytics/changelogs/user-reads',
        method: 'GET',
        params: params ?? undefined
      }),
      providesTags: ['ChangelogUserReadAudit']
    }),
    getChangelogs: builder.query<Pto.Analytics.Changelog[], void>({
      query: () => ({
        url: 'analytics/changelogs',
        method: 'GET'
      }),
      providesTags: ['Changelogs']
    }),
    createChangelog: builder.mutation<Pto.Analytics.Changelog, Pto.Analytics.CreateChangelog>({
      query: (body) => ({
        url: 'analytics/changelogs',
        method: 'POST',
        body
      }),
      invalidatesTags: ['Changelogs', 'ChangelogUnread']
    }),
    updateChangelog: builder.mutation<Pto.Analytics.Changelog, { id: number; body: Pto.Analytics.UpdateChangelog }>({
      query: ({ id, body }) => ({
        url: `analytics/changelogs/${id}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: ['Changelogs', 'ChangelogUnread']
    }),
    deleteChangelog: builder.mutation<void, number>({
      query: (id) => ({
        url: `analytics/changelogs/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Changelogs', 'ChangelogUnread']
    })
  })
})

export const {
  useGetAnalyzedChatsCountQuery,
  useGetAnalyzedChatsQuery,
  useGetChatSessionsCountQuery,
  useGetPersonalizedStartersQuery,
  useResetAnalyzedChatsMutation,
  useGetDashboardFiltersQuery,
  useLazyGetDashboardTutorOptionsQuery,
  useGetDashboardDataQuery,
  useLazyGetChatsCsvQuery,
  useGetLearnerEngagementFileInfoQuery,
  useLazyGetLearnerEngagementFileInfoQuery,
  useRequestLearnerEngagementGenerationMutation,
  useGetChangelogUnreadCountQuery,
  useMarkChangelogsReadMutation,
  useGetChangelogUserReadsAuditQuery,
  useGetChangelogsQuery,
  useCreateChangelogMutation,
  useUpdateChangelogMutation,
  useDeleteChangelogMutation
} = analyticsApi
